import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import Loader from "../../Components/Loader/Loader";
import NotFound from "../NotFound/NotFound";
import {
  playerIdFromParam,
  playerUrl,
  sportUrl,
  teamUrl,
} from "../../Utils/Constants";
import { errorMessage, filterPlayers, getPlayer } from "../../Utils/api";
import "./AthletePage.css";

/**
 * The athlete page — `/p/{player_id}-{slug}`.
 *
 * This did not exist before: the detail view was opened in a new tab from the
 * grid and the fan/brand flows were separate top-level routes. Now one page
 * holds the athlete and both calls to action, which is also what makes the
 * link worth sharing.
 */
export default function AthletePage() {
  const { playerParam } = useParams();
  const navigate = useNavigate();

  const [athlete, setAthlete] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const playerId = playerIdFromParam(playerParam);

  const load = useCallback(async () => {
    if (!playerId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getPlayer(playerId);
      setAthlete(data);

      // The slug is decoration; the id resolved the athlete. If the URL's slug
      // is stale (a name correction, or an old shared link) quietly rewrite it
      // to the canonical form rather than 404ing.
      const canonical = playerUrl(data);
      if (canonical !== `/p/${playerParam}`) {
        navigate(canonical, { replace: true });
      }

      if (data.team_id) {
        const roster = await filterPlayers(
          { team_id: data.team_id },
          { limit: 7 }
        );
        setRelated(
          (roster.filtered_players || []).filter(
            (p) => p.player_id !== data.player_id
          )
        );
      }
    } catch (error) {
      if (error?.response?.status === 404) setNotFound(true);
      else toast.error(errorMessage(error, "Could not load this athlete"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, playerParam]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (athlete) {
      document.title = `${athlete.player_name} · ${athlete.team_name || "NIL-Collab"}`;
    }
  }, [athlete]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: athlete.player_name, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch {
      /* the user dismissed the share sheet; nothing to report */
    }
  };

  if (notFound) return <NotFound />;

  return (
    <div>
      <Header />
      {loading ? <Loader /> : null}

      {athlete ? (
        <div className="athlete-page">
          <div className="athlete-topbar">
            {/* Real browser history, so Back works and returns to the grid
                with its filters intact. */}
            <button className="athlete-back" onClick={() => navigate(-1)}>
              ← Back
            </button>
            <button className="athlete-share" onClick={share}>
              Share
            </button>
          </div>

          <div className="athlete-hero">
            <div className="athlete-photo">
              {athlete.image_url ? (
                <img src={athlete.image_url} alt={athlete.player_name} />
              ) : (
                <div className="athlete-photo-fallback" aria-hidden="true">
                  {(athlete.player_name || "?").charAt(0)}
                </div>
              )}
            </div>

            <div className="athlete-identity">
              <h1>{athlete.player_name}</h1>

              <p className="athlete-affil">
                {athlete.sport_slug && athlete.team_slug ? (
                  <Link to={teamUrl(athlete.sport_slug, athlete.team_slug)}>
                    {athlete.team_name}
                  </Link>
                ) : (
                  athlete.team_name
                )}
                {athlete.sport_slug ? (
                  <>
                    {" · "}
                    <Link to={sportUrl(athlete.sport_slug)}>
                      {athlete.sport_name}
                    </Link>
                  </>
                ) : null}
              </p>

              <ul className="athlete-meta">
                {athlete.jersey_number ? <li>#{athlete.jersey_number}</li> : null}
                {athlete.position ? <li>{athlete.position}</li> : null}
                {athlete.player_class ? <li>{athlete.player_class}</li> : null}
                {athlete.height ? <li>{athlete.height}</li> : null}
                {athlete.weight ? <li>{athlete.weight}</li> : null}
              </ul>

              {athlete.birthplace ? (
                <p className="athlete-home">{athlete.birthplace}</p>
              ) : null}

              {athlete.active_status === false ? (
                <p className="athlete-inactive">
                  Not currently listed on a roster
                </p>
              ) : null}

              {/* One block holding both actions, rather than two parallel
                  funnels. Fan vs brand matters at the moment of action, not
                  while browsing. */}
              <div className="athlete-cta">
                <div className="athlete-cta-head">Support this athlete</div>
                <div className="athlete-cta-buttons">
                  <Link
                    className="athlete-cta-fan"
                    to={`/FanExperience/${athlete.player_id}/${athlete.team_id}`}
                  >
                    I'm a Fan
                  </Link>
                  <Link
                    className="athlete-cta-brand"
                    to={`/BrandPartnership/${athlete.player_id}/${athlete.team_id}`}
                  >
                    I'm a Brand
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {athlete.player_profile_url ? (
            <div className="athlete-links">
              <span>ESPN:</span>
              {[
                ["Profile", athlete.player_profile_url],
                ["Stats", athlete.player_stats_url],
                ["Bio", athlete.player_bio_url],
                ["News", athlete.player_news_url],
              ]
                .filter(([, href]) => href)
                .map(([label, href]) => (
                  // External, so a new tab is the right call here.
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label}
                  </a>
                ))}
            </div>
          ) : null}

          {related.length ? (
            <div className="athlete-related">
              <h2>More from {athlete.team_name}</h2>
              <div className="athlete-related-row">
                {related.map((p) => (
                  <Link key={p.player_id} to={playerUrl(p)} className="related-card">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.player_name} />
                    ) : (
                      <div className="related-fallback" aria-hidden="true">
                        {(p.player_name || "?").charAt(0)}
                      </div>
                    )}
                    <span className="related-name">{p.player_name}</span>
                    <span className="related-pos">
                      {[p.position, p.player_class].filter(Boolean).join(" · ")}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
